export type AssistantQuestionAnswerCheckMessage = {
  id?: string;
  role?: string;
  parent_message_id?: string | null;
  metadata?: Record<string, unknown> | null;
};

type QuestionShape = {
  question?: string;
  inputType?: string;
  options?: Array<{ value?: string; label?: string }>;
};

function questionSignatureFromQuestions(questions: QuestionShape[] | null | undefined): string {
  if (!Array.isArray(questions) || questions.length === 0) return "";
  return questions
    .map((q) => `${String(q.question ?? "")}::${String(q.inputType ?? "")}`)
    .join("||");
}

/** Structural prompt identity from an assistant row's persisted userQuestion metadata. */
export function getAssistantMessageQuestionSignature(
  message: Pick<AssistantQuestionAnswerCheckMessage, "metadata"> | undefined,
): string {
  const userQuestion = message?.metadata?.userQuestion;
  if (!userQuestion || typeof userQuestion !== "object") return "";
  const uq = userQuestion as {
    questions?: QuestionShape[];
    question?: string;
    inputType?: string;
    options?: QuestionShape["options"];
  };
  const questions =
    uq.questions
    ?? (uq.question
      ? [{ question: uq.question, inputType: uq.inputType ?? "button", options: uq.options ?? [] }]
      : null);
  return questionSignatureFromQuestions(questions);
}

/**
 * Only attribute "already answered" to a history row when it matches the live question prompt.
 * Avoids hiding a new streamed ask while an older answered question remains in the thread.
 */
export function resolveQuestionMessageIdForAnsweredCheck(params: {
  liveQuestionSignature: string;
  pendingMessage?: Pick<AssistantQuestionAnswerCheckMessage, "id" | "metadata">;
  latestAssistantQuestionMessage?: Pick<AssistantQuestionAnswerCheckMessage, "id" | "metadata">;
}): string | undefined {
  const matchesLive = (message?: Pick<AssistantQuestionAnswerCheckMessage, "id" | "metadata">) => {
    if (!message?.id) return false;
    if (!params.liveQuestionSignature) return true;
    return getAssistantMessageQuestionSignature(message) === params.liveQuestionSignature;
  };

  if (matchesLive(params.pendingMessage)) return params.pendingMessage!.id;
  if (matchesLive(params.latestAssistantQuestionMessage)) return params.latestAssistantQuestionMessage!.id;
  return undefined;
}

/** True when a user turn was already persisted as a reply to this assistant question. */
export function isAssistantQuestionAnsweredInMessages(
  messages: AssistantQuestionAnswerCheckMessage[],
  questionMessageId: string | undefined,
): boolean {
  if (!questionMessageId) return false;
  return messages.some((m) => m.role === "user" && m.parent_message_id === questionMessageId);
}
